import { plural } from '@padla/core/i18n/plural';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// SVG Icon Components
// ---------------------------------------------------------------------------

function SvgIcon({ size = 24, children }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function IconMic({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </SvgIcon>
  );
}

function IconCamera({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </SvgIcon>
  );
}

function IconCircle({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" />
    </SvgIcon>
  );
}

function IconFile({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </SvgIcon>
  );
}

function IconSearch({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </SvgIcon>
  );
}

function IconImage({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </SvgIcon>
  );
}

function IconMusic({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </SvgIcon>
  );
}

function IconTerminal({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <polyline points="4 17 10 11 4 5" />
      <line x1="12" y1="19" x2="20" y2="19" />
    </SvgIcon>
  );
}

function IconUser({ size = 24 }) {
  return (
    <SvgIcon size={size}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </SvgIcon>
  );
}

// ---------------------------------------------------------------------------
// Animation Components
// ---------------------------------------------------------------------------

const GRAPH_NODES = [
  { cx: 60, cy: 40 },
  { cx: 140, cy: 35 },
  { cx: 45, cy: 90 },
  { cx: 155, cy: 85 },
];

const GRAPH_LINES = [
  [0, 1],
  [0, 2],
  [1, 3],
  [2, 3],
  [0, 3],
];

function GraphAnimation() {
  return (
    <div
      style={{
        height: 120,
        borderRadius: 14,
        overflow: 'hidden',
        background: 'var(--padla-bg-card)',
        border: '1px solid var(--padla-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="200" height="120" viewBox="0 0 200 120" aria-hidden="true">
        {GRAPH_LINES.map(([a, b], i) => (
          <motion.path
            key={`${a}-${b}`}
            d={`M ${GRAPH_NODES[a].cx} ${GRAPH_NODES[a].cy} L ${GRAPH_NODES[b].cx} ${GRAPH_NODES[b].cy}`}
            stroke="var(--padla-accent-dim)"
            strokeWidth="1"
            fill="none"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.4 }}
            transition={{ duration: 1, delay: 0.3 + i * 0.15 }}
          />
        ))}
        {GRAPH_NODES.map((n, i) => (
          <motion.circle
            key={`${n.cx}-${n.cy}`}
            cx={n.cx}
            cy={n.cy}
            r="6"
            fill="var(--padla-accent)"
            opacity="0.7"
            animate={{ scale: [1, 1.15, 1] }}
            transition={{ duration: 3, repeat: Infinity, delay: i * 0.4 }}
          />
        ))}
      </svg>
    </div>
  );
}

const WAVEFORM_BARS = [0, 1, 2, 3, 4];

function WaveformAnimation() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 4,
        height: 48,
      }}
    >
      {WAVEFORM_BARS.map((i) => (
        <motion.div
          key={i}
          style={{
            width: 4,
            height: 32,
            borderRadius: 2,
            background: 'var(--padla-accent)',
            transformOrigin: 'bottom',
          }}
          animate={{ scaleY: [0.35, 1, 0.55, 0.85, 0.35] }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.15,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------------

function SceneTitle({ children }) {
  return (
    <div
      style={{
        fontFamily: 'var(--font-display)',
        fontSize: 28,
        color: 'var(--padla-accent)',
        textAlign: 'center',
      }}
    >
      {children}
    </div>
  );
}

function FeatureCard({ icon, label, children }) {
  return (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: 14,
        background: 'var(--padla-bg-card)',
        border: '1px solid var(--padla-border)',
      }}
    >
      {label && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--padla-accent)',
            marginBottom: 8,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: 'var(--padla-accent)' }}>{icon}</span>
          {label}
        </div>
      )}
      <div style={{ fontSize: 14, color: 'var(--padla-text)', lineHeight: 1.6 }}>{children}</div>
    </div>
  );
}

function FormatCell({ icon, title, desc }) {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 14,
        background: 'var(--padla-bg-card)',
        border: '1px solid var(--padla-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ color: 'var(--padla-accent)' }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--padla-text)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--padla-text-3)', lineHeight: 1.4 }}>{desc}</div>
    </div>
  );
}

function PriceRow({ label, price }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid var(--padla-border)',
      }}
    >
      <span style={{ fontSize: 13, color: 'var(--padla-text)' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--padla-accent)',
          fontFamily: 'var(--font-mono)',
        }}
      >
        {price}
      </span>
    </div>
  );
}

function StyleCard({ name, quote, desc, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3, type: 'spring', stiffness: 300, damping: 25 }}
      style={{
        flex: 1,
        padding: '14px 10px',
        borderRadius: 14,
        textAlign: 'center',
        background: 'var(--padla-bg-card)',
        border: '1px solid var(--padla-border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--padla-text)' }}>{name}</div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--padla-text-2)',
          fontStyle: 'italic',
          lineHeight: 1.3,
          minHeight: 28,
        }}
      >
        {quote}
      </div>
      <div style={{ fontSize: 11, color: 'var(--padla-text-3)' }}>{desc}</div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Scene Definitions (pure components, no state dependencies)
// ---------------------------------------------------------------------------

function SceneWelcome() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        textAlign: 'center',
        gap: 16,
      }}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 36,
          fontWeight: 400,
          letterSpacing: '0.15em',
          color: 'var(--padla-accent)',
        }}
      >
        PADLA
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        style={{ fontSize: 20, color: 'var(--padla-text)', lineHeight: 1.4 }}
      >
        Бесконечная память.
        <br />
        Ни одного забытого слова.
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.4 }}
        style={{ fontSize: 13, color: 'var(--padla-text-3)' }}
      >
        Разберёмся за 2 минуты
      </motion.div>
    </div>
  );
}

function SceneMemory() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SceneTitle>Я помню всё</SceneTitle>
      <GraphAnimation />
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 14,
          color: 'var(--padla-text)',
          lineHeight: 1.6,
        }}
      >
        Каждое сообщение, фото, голосовое — всё становится знанием. Спроси через месяц — найду и
        вспомню.
      </div>
      <div
        style={{
          padding: 14,
          borderRadius: 14,
          background: 'var(--padla-accent-bg)',
          border: '1px solid var(--padla-border-hover)',
          fontSize: 13,
          color: 'var(--padla-text-2)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        Обычные нейросети забывают начало диалога. Я — никогда.
      </div>
    </div>
  );
}

function SceneTalk() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SceneTitle>Как со мной говорить</SceneTitle>
      <div
        style={{
          padding: 18,
          borderRadius: 14,
          background: 'var(--padla-green-bg)',
          border: '1px solid var(--padla-border)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--padla-green)',
            marginBottom: 8,
          }}
        >
          Личный чат
        </div>
        <div style={{ fontSize: 16, color: 'var(--padla-text)' }}>Просто пиши — отвечу на всё</div>
      </div>
      <div
        style={{
          padding: 18,
          borderRadius: 14,
          background: 'var(--padla-accent-bg)',
          border: '1px solid var(--padla-border)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--padla-accent)',
            marginBottom: 8,
          }}
        >
          Группы
        </div>
        <div style={{ fontSize: 16, color: 'var(--padla-text)' }}>
          Напиши <span style={{ fontWeight: 600 }}>Падла</span> в сообщении
        </div>
      </div>
      <div
        style={{
          padding: 12,
          borderRadius: 14,
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 12,
          color: 'var(--padla-text-3)',
          textAlign: 'center',
          lineHeight: 1.4,
        }}
      >
        Ещё можно ответить на моё сообщение или @упомянуть
      </div>
    </div>
  );
}

function SceneFormats() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <SceneTitle>Что я понимаю</SceneTitle>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <FormatCell
          icon={<IconMic size={22} />}
          title="Голосовые"
          desc="Речь, эмоции, фон, контекст"
        />
        <FormatCell
          icon={<IconCamera size={22} />}
          title="Фото"
          desc="Люди, текст, объекты, место"
        />
        <FormatCell
          icon={<IconCircle size={22} />}
          title="Видеокружки"
          desc="Покадровый анализ нативных кружков"
        />
        <FormatCell icon={<IconFile size={22} />} title="PDF" desc="Документы до 5 МБ" />
      </div>
      <div
        style={{
          padding: 12,
          borderRadius: 14,
          background: 'var(--padla-red-bg)',
          border: '1px solid var(--padla-red)',
          fontSize: 12,
          color: 'var(--padla-text)',
          lineHeight: 1.4,
          textAlign: 'center',
        }}
      >
        Обычное видео и аудиофайлы не поддерживаются
      </div>
      <div
        style={{
          padding: 12,
          borderRadius: 14,
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 12,
          color: 'var(--padla-text-3)',
          textAlign: 'center',
          lineHeight: 1.4,
        }}
      >
        Скинул файлы? Подожди минуту перед вопросом — мне нужно время на обработку
      </div>
    </div>
  );
}

function ScenePowers() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SceneTitle>Суперсилы</SceneTitle>
      <FeatureCard icon={<IconSearch size={18} />} label="Интернет">
        Попроси «поищи в интернете» — найду свежую информацию с источниками.
      </FeatureCard>
      <FeatureCard icon={<IconImage size={18} />} label="Картинки">
        Скажи «нарисуй» — сгенерирую изображение. Ответь на картинку «измени» — отредактирую.
      </FeatureCard>
    </div>
  );
}

function SceneMusic() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <SceneTitle>Создаю музыку</SceneTitle>
      <WaveformAnimation />
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          width: '100%',
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 14,
          color: 'var(--padla-text)',
          lineHeight: 1.6,
        }}
      >
        Опиши настроение или напиши текст — создам оригинальный трек. Можно уточнять стиль, менять
        слова, пробовать снова.
      </div>
      <div style={{ fontSize: 11, color: 'var(--padla-text-3)' }}>Powered by Suno</div>
    </div>
  );
}

function SceneAgent() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <SceneTitle>AI-агент</SceneTitle>
      <div style={{ color: 'var(--padla-accent)' }}>
        <IconTerminal size={40} />
      </div>
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          width: '100%',
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 14,
          color: 'var(--padla-text)',
          lineHeight: 1.6,
        }}
      >
        Исследования, анализ файлов, презентации, код. Работает дольше обычного ответа — зато делает
        по-настоящему.
      </div>
      <div style={{ fontSize: 12, color: 'var(--padla-text-3)', textAlign: 'center' }}>
        1–15 кредитов в зависимости от сложности
      </div>
    </div>
  );
}

function ScenePersona() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
      <SceneTitle>Я знаю тебя</SceneTitle>
      <div style={{ color: 'var(--padla-accent)' }}>
        <IconUser size={40} />
      </div>
      <div
        style={{
          padding: 16,
          borderRadius: 14,
          width: '100%',
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
          fontSize: 14,
          color: 'var(--padla-text)',
          lineHeight: 1.6,
        }}
      >
        Чем больше общаешься — тем точнее профиль. Интересы, привычки, стиль общения.
      </div>
      <div style={{ fontSize: 12, color: 'var(--padla-text-3)' }}>Загляни в «Персона» в панели</div>
    </div>
  );
}

function SceneStyle() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <SceneTitle>Три характера</SceneTitle>
      <div style={{ display: 'flex', gap: 8 }}>
        <StyleCard
          name="Джентльмен"
          quote={`\u00ABПозвольте не согласиться\u00BB`}
          desc="Вежливый и точный"
          delay={0}
        />
        <StyleCard
          name="Уличный"
          quote={`\u00ABСлушай, он гонит\u00BB`}
          desc="Прямой и дерзкий"
          delay={0.1}
        />
        <StyleCard
          name="Мясник"
          quote={`\u00ABЗасунь свои аргументы...\u00BB`}
          desc="Без фильтров"
          delay={0.2}
        />
      </div>
      <div style={{ fontSize: 12, color: 'var(--padla-text-3)', textAlign: 'center' }}>
        Переключай в «Стиль» — для каждого чата отдельно
      </div>
    </div>
  );
}

function SceneReady({ onComplete, onClose, welcomeGrant, welcomeTtlDays }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SceneTitle>Всё готово</SceneTitle>
      <div
        style={{
          padding: '4px 18px',
          borderRadius: 14,
          background: 'var(--padla-bg-card)',
          border: '1px solid var(--padla-border)',
        }}
      >
        {/* Цены — те же, что в плашке кабинета (pages/UserBalance.tsx, PRICE_ROWS):
            вывод из PRICES биллинга + calcImageCredits. Держать в согласии. */}
        <PriceRow label="Любой ответ Падлы" price="1 кр" />
        <PriceRow label="Нарисовать картинку" price="1 кр" />
        <PriceRow label="Астрологический разбор" price="1 кр" />
        <PriceRow label="Сочинить песню" price="1 кр" />
        <PriceRow label="Разбор документа" price="1 кр" />
        <PriceRow label="Большая работа" price="1–15 кр" />
        <PriceRow label="Голосовые, фото и видео" price="бесплатно" />
      </div>
      <div
        style={{ fontSize: 12, color: 'var(--padla-text-3)', textAlign: 'center', lineHeight: 1.4 }}
      >
        Падла бесплатно смотрит и слушает всё, что вы шлёте. Он всё запомнит, даже если промолчит
        или на балансе пусто. Платный только ответ.
      </div>
      {/* Число и срок — из кассы (/billing/pricing → SSOT базы), не литерал: до 3 сен 2026
          экран обещал 20 при выданных 5/20/30 по эпохам. Нет чисел — строки нет. Текст —
          редакция Gemini (протокол копирайта), склонения — plural. */}
      {welcomeGrant != null && welcomeTtlDays != null && (
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--padla-accent)',
            textAlign: 'center',
          }}
        >
          Стартовый баланс: {welcomeGrant} {plural(welcomeGrant, 'кредит', 'кредита', 'кредитов')}{' '}
          на {welcomeTtlDays} {plural(welcomeTtlDays, 'день', 'дня', 'дней')}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          type="button"
          onClick={onComplete}
          style={{
            flex: 1,
            padding: '14px 0',
            borderRadius: 12,
            border: 'none',
            background: 'var(--padla-accent)',
            color: '#fff',
            fontSize: 14,
            fontWeight: 600,
            fontFamily: 'var(--font-body)',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
        >
          Открыть чат
        </button>
        <button
          type="button"
          onClick={onClose}
          style={{
            flex: 1,
            padding: '14px 0',
            borderRadius: 12,
            background: 'var(--padla-bg-card)',
            border: '1px solid var(--padla-border)',
            color: 'var(--padla-text-2)',
            fontSize: 14,
            fontFamily: 'var(--font-body)',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
        >
          В меню
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slide animation variants (static -- no need to recreate per render)
// ---------------------------------------------------------------------------

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? 250 : -250, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? -250 : 250, opacity: 0 }),
};

// ---------------------------------------------------------------------------
// Main Tutorial Component
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onComplete: () => void,
 *   onClose: () => void,
 *   welcomeGrant?: number | null,
 *   welcomeTtlDays?: number | null,
 * }} props
 */
export default function Tutorial({
  onComplete,
  onClose,
  welcomeGrant = null,
  welcomeTtlDays = null,
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);

  const scenes = useMemo(
    () => [
      <SceneWelcome key="welcome" />,
      <SceneMemory key="memory" />,
      <SceneTalk key="talk" />,
      <SceneFormats key="formats" />,
      <ScenePowers key="powers" />,
      <SceneMusic key="music" />,
      <SceneAgent key="agent" />,
      <ScenePersona key="persona" />,
      <SceneStyle key="style" />,
      <SceneReady
        key="ready"
        onComplete={onComplete}
        onClose={onClose}
        welcomeGrant={welcomeGrant}
        welcomeTtlDays={welcomeTtlDays}
      />,
    ],
    [onComplete, onClose]
  );

  const TOTAL = scenes.length;
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === TOTAL - 1;

  function haptic() {
    window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light');
  }

  const goTo = useCallback(function goTo(idx) {
    setCurrentSlide((prev) => {
      setDirection(idx > prev ? 1 : -1);
      return idx;
    });
    haptic();
  }, []);

  const handleNext = useCallback(
    function handleNext() {
      setCurrentSlide((prev) => {
        if (prev >= TOTAL - 1) return prev;
        setDirection(1);
        return prev + 1;
      });
      haptic();
    },
    [TOTAL]
  );

  const handlePrev = useCallback(function handlePrev() {
    setCurrentSlide((prev) => {
      if (prev <= 0) return prev;
      setDirection(-1);
      return prev - 1;
    });
    haptic();
  }, []);

  const handleDragEnd = useCallback(
    function handleDragEnd(_, info) {
      if (info.offset.x < -50 || info.velocity.x < -500) handleNext();
      else if (info.offset.x > 50 || info.velocity.x > 500) handlePrev();
    },
    [handleNext, handlePrev]
  );

  useEffect(
    function handleKeyboardNav(e) {
      function onKeyDown(e) {
        if (e.key === 'ArrowRight') handleNext();
        else if (e.key === 'ArrowLeft') handlePrev();
      }
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    },
    [handleNext, handlePrev]
  );

  return (
    <div
      role="dialog"
      aria-label="Обучение PADLA"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        background: 'var(--padla-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        fontFamily: 'var(--font-body)',
        color: 'var(--padla-text)',
      }}
    >
      {/* Progress bar */}
      <div
        role="progressbar"
        aria-valuenow={currentSlide + 1}
        aria-valuemin={1}
        aria-valuemax={TOTAL}
        aria-label={`Шаг ${currentSlide + 1} из ${TOTAL}`}
        style={{ width: '100%', maxWidth: '28rem', padding: '12px 20px 0' }}
      >
        <div style={{ display: 'flex', gap: 3 }}>
          {Array.from({ length: TOTAL }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: деталь рисунка (деление, точка, полоска, пустая клетка): её место и есть имя, набор не переставляется
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: 'var(--padla-border)',
                overflow: 'hidden',
              }}
            >
              <motion.div
                style={{ height: '100%', background: 'var(--padla-accent)', borderRadius: 2 }}
                initial={{ width: 0 }}
                animate={{ width: i <= currentSlide ? '100%' : 0 }}
                transition={{ duration: 0.3 }}
              />
            </div>
          ))}
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 6,
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--padla-text-3)' }}>
            {currentSlide + 1} / {TOTAL}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Пропустить обучение"
            style={{
              fontSize: 12,
              color: 'var(--padla-text-3)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-body)',
              padding: '2px 0',
            }}
          >
            Пропустить
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        style={{
          flex: 1,
          width: '100%',
          maxWidth: '28rem',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={handleDragEnd}
            style={{
              padding: '16px 20px',
              height: '100%',
              overflowY: 'auto',
              cursor: 'grab',
            }}
          >
            {scenes[currentSlide]}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation buttons */}
      <div
        style={{
          width: '100%',
          maxWidth: '28rem',
          padding: '12px 20px 20px',
          display: 'flex',
          gap: 10,
          borderTop: '1px solid var(--padla-border)',
        }}
      >
        {!isFirst && (
          <button
            type="button"
            onClick={handlePrev}
            aria-label="Предыдущий слайд"
            style={{
              padding: '12px 20px',
              borderRadius: 12,
              background: 'var(--padla-bg-card)',
              border: '1px solid var(--padla-border)',
              color: 'var(--padla-text-2)',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
            }}
          >
            Назад
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            onClick={handleNext}
            aria-label="Следующий слайд"
            style={{
              flex: 1,
              padding: '12px 20px',
              borderRadius: 12,
              border: 'none',
              background: 'var(--padla-accent)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
            }}
          >
            Далее
          </button>
        )}
      </div>
    </div>
  );
}
